import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import Button from "../../ui/Button.tsx";
import Drawer from "../../ui/Drawer.tsx";
import Modal from "../../ui/Modal.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import StatusBadge, { statusColor, StatusCirurgia } from "../../ui/StatusBadge.tsx";
import NovaCirurgiaForm from "../../components/CadastroCirurgia/NovaCirurgiaForm.tsx";
import CirurgiaDetailsModal from "../../components/Modals/CirurgiaDetailsModal/index.tsx";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { Cirurgia, STATUS_CIRURGIA } from "../../models/Cirurgia.ts";
import { alterarStatusCirurgia, STATUS_LABEL } from "../../utils/statusCirurgia.util.ts";
import "../../styles/design-system.css";

type Visao = "semana" | "dia" | "mes";

const fmt = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfWeek = (d: Date) => {
  const date = new Date(d);
  date.setDate(date.getDate() - date.getDay());
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addDays = (d: Date, n: number) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};
const dotVar = (status: string) =>
  `var(--${statusColor(status) === "neutral" ? "ink-faint" : statusColor(status)})`;

const DOW = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const MES_NOME = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const MES_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

const AgendaPage = () => {
  const [visao, setVisao] = useState<Visao>("semana");
  const [refDate, setRefDate] = useState(new Date());
  const [cirurgias, setCirurgias] = useState<Cirurgia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [novaCirurgiaAberta, setNovaCirurgiaAberta] = useState(false);
  const [novaCirurgiaEnviando, setNovaCirurgiaEnviando] = useState(false);

  // Detalhe da cirurgia. Reusa o CirurgiaDetailsModal da tela de Cirurgias, que
  // já tem modo leitura e modo edição no mesmo componente.
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [cirurgiaSelecionada, setCirurgiaSelecionada] = useState<Cirurgia | null>(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [acaoCarregando, setAcaoCarregando] = useState(false);

  // Cancelamento: o motivo é obrigatório, então passa por um modal próprio.
  const [cancelAberto, setCancelAberto] = useState(false);
  const [motivo, setMotivo] = useState("");

  // Status desmarcados na legenda. Guardar os OCULTOS e não os visíveis faz o
  // estado inicial (nada oculto = tudo aparece) não depender da lista de status.
  const [statusOcultos, setStatusOcultos] = useState<Set<StatusCirurgia>>(new Set());

  const { tipo, erroAoCarregar } = usePermissoes();
  const { showFeedback } = useFeedback();

  // Mesma regra da tela de Cirurgias (listagemCirurgia.tsx): quando não dá para
  // confirmar a função do usuário, libera — quem barra de verdade é o servidor.
  const podeCriarOuEditar = erroAoCarregar || tipo === "ADM" || tipo === "OPERADOR";
  const podeCancelar = podeCriarOuEditar;

  // Popover de seleção rápida de mês/ano (item do toolbar).
  const [quickJumpAberto, setQuickJumpAberto] = useState(false);
  const [quickJumpAno, setQuickJumpAno] = useState(() => new Date().getFullYear());
  const quickJumpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!quickJumpAberto) return;
    setQuickJumpAno(refDate.getFullYear());
    const aoClicarFora = (e: MouseEvent) => {
      if (quickJumpRef.current && !quickJumpRef.current.contains(e.target as Node)) {
        setQuickJumpAberto(false);
      }
    };
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [quickJumpAberto, refDate]);

  const pularParaMes = (mesIndex: number) => {
    setRefDate(new Date(quickJumpAno, mesIndex, 1));
    setQuickJumpAberto(false);
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(refDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [refDate]);

  const visibleDays = useMemo(() => {
    if (visao === "dia") return [refDate];
    if (visao === "semana") return weekDays;
    return [];
  }, [visao, refDate, weekDays]);

  const rangeInicial = useMemo(() => {
    if (visao === "dia") return refDate;
    if (visao === "semana") return weekDays[0];
    return startOfMonth(refDate);
  }, [visao, refDate, weekDays]);

  const rangeFinal = useMemo(() => {
    if (visao === "dia") return refDate;
    if (visao === "semana") return weekDays[6];
    return endOfMonth(refDate);
  }, [visao, refDate, weekDays]);

  const fetchCirurgias = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: "0",
        size: "500",
        dataInicial: fmt(rangeInicial),
        dataFinal: fmt(rangeFinal),
      });
      const response = await handleService({ method: MethodsEnum.GET, endpoint: `/cirurgia?${params.toString()}` });
      setCirurgias(response?.data?.content || []);
      setErro(null);
    } catch (e: any) {
      // Antes este catch era vazio: qualquer falha virava "grade vazia", e até a
      // mensagem já formatada pelo interceptor do apiService (403 de permissão,
      // erro de validação) era engolida sem o usuário ver nada.
      setCirurgias([]);
      setErro(e?.message || "Não foi possível carregar a agenda.");
    } finally {
      setIsLoading(false);
    }
  }, [rangeInicial, rangeFinal]);

  useEffect(() => {
    fetchCirurgias();
  }, [fetchCirurgias]);

  // Contagem por status do período inteiro — alimenta os números da legenda e
  // não muda quando o usuário filtra, senão o filtro esconderia o próprio contador.
  const contagemPorStatus = useMemo(() => {
    const contagem = new Map<string, number>();
    cirurgias.forEach((c) => contagem.set(c.status, (contagem.get(c.status) || 0) + 1));
    return contagem;
  }, [cirurgias]);

  const cirurgiasVisiveis = useMemo(
    () => (statusOcultos.size === 0 ? cirurgias : cirurgias.filter((c) => !statusOcultos.has(c.status))),
    [cirurgias, statusOcultos]
  );

  const alternarStatus = (status: StatusCirurgia) => {
    setStatusOcultos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(status)) proximo.delete(status);
      else proximo.add(status);
      return proximo;
    });
  };

  const porDia = useMemo(() => {
    const map = new Map<string, Cirurgia[]>();
    cirurgiasVisiveis.forEach((c) => {
      const key = c.data.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    });
    map.forEach((list) => list.sort((a, b) => a.data.localeCompare(b.data)));
    return map;
  }, [cirurgiasVisiveis]);

  const todayKey = fmt(new Date());

  const navegar = (direcao: 1 | -1) => {
    setRefDate((d) => {
      if (visao === "dia") return addDays(d, direcao);
      if (visao === "semana") return addDays(d, direcao * 7);
      return new Date(d.getFullYear(), d.getMonth() + direcao, 1);
    });
  };

  const rangeLabel = useMemo(() => {
    if (visao === "dia") {
      return refDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
    }
    if (visao === "semana") {
      const a = weekDays[0];
      const b = weekDays[6];
      const mesmoMes = a.getMonth() === b.getMonth();
      return mesmoMes
        ? `${a.getDate()} — ${b.getDate()} de ${MES_NOME[a.getMonth()]}`
        : `${a.getDate()} de ${MES_NOME[a.getMonth()]} — ${b.getDate()} de ${MES_NOME[b.getMonth()]}`;
    }
    return `${MES_NOME[refDate.getMonth()]} de ${refDate.getFullYear()}`;
  }, [visao, refDate, weekDays]);

  const monthGridDays = useMemo(() => {
    const first = startOfMonth(refDate);
    const last = endOfMonth(refDate);
    const start = startOfWeek(first);
    const totalCells = Math.ceil((last.getDate() + first.getDay()) / 7) * 7;
    return Array.from({ length: totalCells }, (_, i) => addDays(start, i));
  }, [refDate]);

  const irParaDia = (d: Date) => {
    setRefDate(d);
    if (visao === "mes") setVisao("dia");
  };

  const abrirDetalhes = (c: Cirurgia) => {
    // Sem buscar de novo: o DTO da listagem (DadosListarCirurgias) é um superset
    // do de detalhe, então o objeto que já está em memória tem tudo que o modal
    // mostra, inclusive observação, motivo e os ids para o modo de edição.
    setCirurgiaSelecionada(c);
    setModoEdicao(false);
    setDetalheAberto(true);
  };

  const fecharDetalhes = () => {
    setDetalheAberto(false);
    setModoEdicao(false);
    setCirurgiaSelecionada(null);
  };

  const salvarEdicao = async (dados: any) => {
    await handleService({
      method: MethodsEnum.PATCH,
      endpoint: "/cirurgia/alterar",
      data: {
        id: dados.id,
        data: dados.data,
        medico: dados.medico || null,
        paciente: dados.paciente || null,
        instrumentador1: dados.instrumentador1 || null,
        instrumentador2: dados.instrumentador2 || null,
        instrumentador3: dados.instrumentador3 || null,
        hospital: dados.hospital || null,
        convenio: dados.convenio || null,
        tipo: dados.tipoCirurgia,
        observacao: dados.observacao || null,
        observacaoTipoCirurgia: dados.observacaoTipoCirurgia || null,
      },
    });
    fetchCirurgias();
  };

  const mudarStatus = async (status: StatusCirurgia, motivoTexto = "") => {
    if (!cirurgiaSelecionada) return;
    setAcaoCarregando(true);
    try {
      await alterarStatusCirurgia(cirurgiaSelecionada.id, status, motivoTexto, "/agenda");
      showFeedback(
        status === "CANCELADA" ? "Cirurgia cancelada." : `Status alterado para "${STATUS_LABEL[status]}".`,
        "success"
      );
      fecharDetalhes();
      setCancelAberto(false);
      setMotivo("");
      fetchCirurgias();
    } catch (e: any) {
      showFeedback(e?.message || "Não foi possível alterar o status.", "error");
    } finally {
      setAcaoCarregando(false);
    }
  };

  const renderDiaCard = (c: Cirurgia) => {
    const hora = new Date(c.data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    const equipe = `${c.medico || "—"} · ${c.hospital || "—"}`;
    // Na visão Dia o card é largo o bastante para carregar médico, hospital,
    // tipo e convênio em linhas próprias. Na Semana a coluna tem ~160px, então
    // lá continua só o essencial, com o resto no title.
    const detalhado = visao === "dia";
    return (
      <button
        key={c.id}
        type="button"
        className="ds-agenda-chip"
        style={{ "--dotcolor": dotVar(c.status) } as React.CSSProperties}
        onClick={() => abrirDetalhes(c)}
        title={`${hora} — ${c.paciente || "Paciente não informado"}\n${equipe}\n${STATUS_LABEL[c.status] || c.status}`}
      >
        <div className="t">{hora}</div>
        <StatusBadge status={c.status} />
        <div className="meio">
          <div className="p">{c.paciente || "Paciente não informado"}</div>
          {!detalhado && <div className="h">{equipe}</div>}
        </div>
        {detalhado && (
          <div className="extra">
            <span><BadgeOutlinedIcon fontSize="inherit" />{c.medico || "Sem médico"}</span>
            <span><LocalHospitalOutlinedIcon fontSize="inherit" />{c.hospital || "Sem hospital"}</span>
            <span><MedicalServicesOutlinedIcon fontSize="inherit" />{c.tipoCirurgia || "Sem tipo"}</span>
            {c.convenio && <span><HealthAndSafetyOutlinedIcon fontSize="inherit" />{c.convenio}</span>}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Agenda</h1>
          <p>Visualize os procedimentos agendados por dia, semana ou mês.</p>
        </div>
        <div className="ds-page-head-actions">
          {/* Antes este botão aparecia para todo mundo: o INSTRUMENTADOR, que não
              tem CIRURGIA_CRIAR, preenchia o formulário inteiro e só levava 403 no
              envio. Mesma guarda já usada em listagemCirurgia.tsx. */}
          {podeCriarOuEditar && (
            <Button variant="primary" size="sm" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setNovaCirurgiaAberta(true)}>
              Nova cirurgia
            </Button>
          )}
        </div>
      </div>

      <div className="ds-toolbar">
        <div className="ds-view-toggle">
          {(["semana", "dia", "mes"] as Visao[]).map((v) => (
            <button
              key={v}
              type="button"
              className={visao === v ? "active" : ""}
              onClick={() => setVisao(v)}
            >
              {v === "semana" ? "Semana" : v === "dia" ? "Dia" : "Mês"}
            </button>
          ))}
        </div>

        <div className="ds-week-nav">
          <button className="ds-icon-btn" onClick={() => navegar(-1)} title="Período anterior">
            <ChevronLeftIcon fontSize="small" />
          </button>

          <div className="ds-dropdown" ref={quickJumpRef}>
            <button
              type="button"
              className="ds-quickjump-trigger"
              onClick={() => setQuickJumpAberto((v) => !v)}
              title="Ir para mês/ano"
            >
              <CalendarMonthOutlinedIcon fontSize="small" />
              <strong>{rangeLabel}</strong>
            </button>
            {quickJumpAberto && (
              <div className="ds-dropdown-menu ds-quickjump-menu">
                <div className="ds-quickjump-year">
                  <button className="ds-icon-btn" onClick={() => setQuickJumpAno((a) => a - 1)} title="Ano anterior">
                    <ChevronLeftIcon fontSize="small" />
                  </button>
                  <strong>{quickJumpAno}</strong>
                  <button className="ds-icon-btn" onClick={() => setQuickJumpAno((a) => a + 1)} title="Próximo ano">
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
                <div className="ds-quickjump-months">
                  {MES_ABREV.map((m, i) => {
                    const ativo = refDate.getFullYear() === quickJumpAno && refDate.getMonth() === i;
                    return (
                      <button
                        key={m}
                        type="button"
                        className={ativo ? "active" : ""}
                        onClick={() => pularParaMes(i)}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button className="ds-icon-btn" onClick={() => navegar(1)} title="Próximo período">
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
        <Button variant="outline" size="sm" icon={<TodayOutlinedIcon fontSize="small" />} onClick={() => setRefDate(new Date())}>
          Hoje
        </Button>
      </div>

      {/* A legenda saiu do painel da direita e virou filtro: cada item liga ou
          desliga aquele status na grade. O número é do período inteiro e não
          muda com o filtro, senão o próprio contador sumiria ao desmarcar. */}
      <div className="ds-agenda-legend" role="group" aria-label="Filtrar por status">
        <span className="lbl">Legenda</span>
        {STATUS_CIRURGIA.map(({ status, label }) => {
          const oculto = statusOcultos.has(status);
          const quantos = contagemPorStatus.get(status) || 0;
          return (
            <button
              key={status}
              type="button"
              className={`ds-agenda-legend-item ${oculto ? "off" : ""}`}
              style={{ "--dotcolor": dotVar(status) } as React.CSSProperties}
              onClick={() => alternarStatus(status)}
              aria-pressed={!oculto}
              title={oculto ? `Mostrar ${label}` : `Ocultar ${label}`}
            >
              <span className="ds-agenda-legend-dot" />
              {label}
              <span className="n">{quantos}</span>
            </button>
          );
        })}
        {statusOcultos.size > 0 && (
          <button type="button" className="ds-agenda-legend-clear" onClick={() => setStatusOcultos(new Set())}>
            Mostrar todas
          </button>
        )}
      </div>

      {erro && <ErrorBanner message={erro} />}

      <div className="ds-agenda-layout">
        <div className="ds-agenda-main">
          {isLoading ? (
            <div className={`ds-week-grid ${visao === "dia" ? "lista" : ""}`}>
              {(visao === "dia" ? [0] : [0, 1, 2, 3, 4, 5, 6]).map((i) => (
                <div className="ds-week-day-col" key={i}>
                  <div className="ds-week-col-head">
                    <div className="ds-skeleton" style={{ height: 10, width: "50%", margin: "0 auto 6px" }} />
                    <div className="ds-skeleton" style={{ height: 18, width: "35%", margin: "0 auto" }} />
                  </div>
                  {[0, 1, 2].map((j) => (
                    <div className="ds-skeleton" key={j} style={{ height: 58, marginBottom: 8, borderRadius: 8 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : visao === "mes" ? (
            <>
              <div className="ds-month-dow">
                {DOW.map((d, i) => <div key={d} className={i === 0 || i === 6 ? "weekend" : ""}>{d}</div>)}
              </div>
              <div className="ds-month-grid">
                {monthGridDays.map((d) => {
                  const key = fmt(d);
                  const itens = porDia.get(key) || [];
                  const isToday = key === todayKey;
                  const foraDoMes = d.getMonth() !== refDate.getMonth();
                  const visiveis = itens.slice(0, 2);
                  const resto = itens.length - visiveis.length;
                  return (
                    <div
                      key={key}
                      className={[
                        "ds-month-cell",
                        foraDoMes ? "outside" : "",
                        isWeekend(d) ? "weekend" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => irParaDia(d)}
                    >
                      <div className={`num ${isToday ? "today" : ""}`}>{d.getDate()}</div>
                      {visiveis.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="entry"
                          style={{ "--dotcolor": dotVar(c.status) } as React.CSSProperties}
                          // A célula inteira navega para o dia; sem parar a
                          // propagação, clicar na cirurgia faria as duas coisas.
                          onClick={(e) => { e.stopPropagation(); abrirDetalhes(c); }}
                          title={`${c.paciente} — ${c.medico || "—"}`}
                        >
                          {new Date(c.data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} {c.paciente}
                        </button>
                      ))}
                      {resto > 0 && <div className="more">+{resto} mais</div>}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={`ds-week-grid ${visao === "dia" ? "lista" : ""}`}>
              {visibleDays.map((d) => {
                const key = fmt(d);
                const itens = porDia.get(key) || [];
                const isToday = key === todayKey;
                return (
                  <div key={key} className={`ds-week-day-col ${isWeekend(d) ? "weekend" : ""}`}>
                    <div className={`ds-week-col-head ${isToday ? "today" : ""}`}>
                      <div className="dow">{DOW[d.getDay()]}</div>
                      <div className="num">{d.getDate()}</div>
                      {/* Substitui o "Sem cirurgias" repetido em cada coluna. */}
                      <div className="qtd">
                        {itens.length === 0 ? "—" : itens.length === 1 ? "1 cirurgia" : `${itens.length} cirurgias`}
                      </div>
                    </div>
                    <div className="ds-week-col-body">{itens.map(renderDiaCard)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Uma mensagem para a semana inteira, em vez de sete "Sem cirurgias". */}
          {!isLoading && !erro && visao !== "mes" && cirurgiasVisiveis.length === 0 && (
            <EmptyState
              title={statusOcultos.size > 0 ? "Nada com os status marcados" : "Nenhuma cirurgia neste período"}
              description={
                statusOcultos.size > 0
                  ? "Ajuste os status na legenda acima para ver o restante."
                  : "Navegue para outro período ou agende um novo procedimento."
              }
              icon={<CalendarMonthOutlinedIcon />}
              actionLabel={podeCriarOuEditar && statusOcultos.size === 0 ? "Nova cirurgia" : undefined}
              onAction={podeCriarOuEditar && statusOcultos.size === 0 ? () => setNovaCirurgiaAberta(true) : undefined}
            />
          )}
        </div>

      </div>

      <Drawer
        open={novaCirurgiaAberta}
        onClose={() => setNovaCirurgiaAberta(false)}
        title="Nova cirurgia"
        subtitle="Cadastre um novo procedimento cirúrgico."
        wide
        footer={
          <>
            <Button variant="outline" onClick={() => setNovaCirurgiaAberta(false)}>Cancelar</Button>
            <Button type="submit" form="nova-cirurgia-form" variant="primary" disabled={novaCirurgiaEnviando}>
              {novaCirurgiaEnviando ? "Agendando..." : "Agendar cirurgia"}
            </Button>
          </>
        }
      >
        <NovaCirurgiaForm
          onSuccess={() => { setNovaCirurgiaAberta(false); fetchCirurgias(); }}
          onSubmittingChange={setNovaCirurgiaEnviando}
        />
      </Drawer>

      <CirurgiaDetailsModal
        open={detalheAberto}
        onClose={fecharDetalhes}
        cirurgia={cirurgiaSelecionada}
        modoEdicao={modoEdicao}
        onSalvar={salvarEdicao}
        acoesLeitura={
          cirurgiaSelecionada && (
            <>
              {/* O destrutivo vai para a esquerda, separado dos outros, e em
                  ghost: em vermelho sólido ele puxava o olho mais que o Editar,
                  que é a ação que se quer de fato a partir de um detalhe. */}
              {podeCancelar && cirurgiaSelecionada.status !== "CANCELADA" && (
                <Button
                  variant="ghost"
                  className="ds-btn-destrutivo"
                  icon={<ClearOutlinedIcon fontSize="small" />}
                  disabled={acaoCarregando}
                  onClick={() => { setMotivo(""); setCancelAberto(true); }}
                >
                  Cancelar cirurgia
                </Button>
              )}
              {/* Sem guarda de permissão: CIRURGIA_ALTERAR_STATUS vale também
                  para o INSTRUMENTADOR, que é justamente quem separa material. */}
              {cirurgiaSelecionada.status !== "REALIZADA" && cirurgiaSelecionada.status !== "CANCELADA" && (
                <Button
                  variant="outline"
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  disabled={acaoCarregando}
                  onClick={() => mudarStatus(cirurgiaSelecionada.status === "SEPARADO" ? "REALIZADA" : "SEPARADO")}
                >
                  {/* Verbo curto em vez de "Marcar como separada": com quatro
                      botões, aquele rótulo sozinho comia 208px dos 516 úteis do
                      rodapé e empurrava o "Fechar" para uma segunda linha. O
                      ícone e o status logo acima no painel dão o contexto. */}
                  {cirurgiaSelecionada.status === "SEPARADO" ? "Concluir" : "Separar"}
                </Button>
              )}
              {podeCriarOuEditar && (
                <Button variant="primary" icon={<EditOutlinedIcon fontSize="small" />} onClick={() => setModoEdicao(true)}>
                  Editar
                </Button>
              )}
            </>
          )
        }
      />

      <Modal
        open={cancelAberto}
        onClose={() => setCancelAberto(false)}
        title="Cancelar cirurgia"
        tone="danger"
        actions={
          <>
            <Button variant="outline" onClick={() => setCancelAberto(false)}>Voltar</Button>
            <Button
              variant="danger"
              disabled={acaoCarregando || !motivo.trim()}
              onClick={() => mudarStatus("CANCELADA", motivo.trim())}
            >
              {acaoCarregando ? "Cancelando..." : "Confirmar cancelamento"}
            </Button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>
          {cirurgiaSelecionada
            ? `Cirurgia de ${cirurgiaSelecionada.paciente}, marcada para ${new Date(cirurgiaSelecionada.data).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.`
            : ""}
        </p>
        <label className="ds-field-label" htmlFor="motivo-cancelamento">Motivo do cancelamento</label>
        <textarea
          id="motivo-cancelamento"
          className="ds-input"
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Explique por que a cirurgia foi cancelada"
        />
      </Modal>
    </div>
  );
};

export default AgendaPage;
